import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  type TooltipContentProps,
  XAxis,
  YAxis,
} from "recharts";
import { useSearchParams } from "react-router";
import { ChartTooltip } from "nazare-ui/components/chart-tooltip";
import { PageSection } from "nazare-ui/components/page-section";

import { SkeletonBlock } from "../../../skeleton-block";
import { useQaAggregates, type AggregateWindow } from "../../../qa-automation-api";

import styles from "./charts.module.css";

// Themed via design tokens (packages/ui/src/utils/theme/tokens.css) so these
// charts stay legible in dark mode instead of hard-coding light-theme hex.
const PASS_COLOR = "var(--green)";
const PASS_FILL = "var(--green-bg)";
const PWC_COLOR = "var(--orange)";
const PWC_FILL = "var(--orange-bg)";
const FAIL_COLOR = "var(--red)";
const FAIL_FILL = "var(--red-bg)";
// No existing status token is close to the original #2d6e57 dark teal (all
// existing accent tokens are more saturated/brighter), so this uses a new
// `--teal`/`--dark-teal` token pair added alongside the other status colours.
const DURATION_COLOR = "var(--teal)";
const AXIS_COLOR = "var(--subtle)";
const GRID_COLOR = "var(--nzr-border)";

function formatDateTick(raw: string): string {
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

function formatSeconds(ms: number): string {
  return `${Math.round(ms / 1000)}s`;
}

/** Live "Verdict trend" and "Average run duration" charts. */
export function QaAutomationCharts() {
  const [searchParams] = useSearchParams();
  const requestedWindow = searchParams.get("window");
  const window: AggregateWindow = ["today", "7d", "30d", "6m", "all"].includes(requestedWindow ?? "")
    ? (requestedWindow as AggregateWindow)
    : "30d";
  const { data, isLoading } = useQaAggregates(window);
  const verdictTrend = (data?.buckets ?? []).map((bucket) => {
    const total = bucket.pass + bucket.passWithSubFinding + bucket.failRegression + bucket.naScope;
    const pct = (value: number) => (total ? (value / total) * 100 : 0);
    return {
      date: bucket.date,
      pass: pct(bucket.pass),
      passWithComment: pct(bucket.passWithSubFinding),
      fail: pct(bucket.failRegression),
    };
  });
  const durationData = (data?.buckets ?? []).map(({ date, avgDurationMs }) => ({ date, avgDurationMs }));

  return (
    <div className={styles.grid}>
      <PageSection title="Verdict trend" padded>
        {isLoading ? (
          <SkeletonBlock width="100%" height={260} />
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={verdictTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11 }}
                stroke={AXIS_COLOR}
                tickFormatter={formatDateTick}
              />
              <YAxis
                stroke={AXIS_COLOR}
                tick={{ fontSize: 11 }}
                domain={[0, 100]}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip
                content={(props: TooltipContentProps<number, string>) => (
                  <ChartTooltip
                    {...props}
                    labelFormatter={(label) => (typeof label === "string" ? formatDateTick(label) : "")}
                    valueFormatter={(value) => (typeof value === "number" ? formatPercent(value) : value)}
                  />
                )}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area
                type="monotone"
                dataKey="fail"
                stackId="verdict"
                stroke={FAIL_COLOR}
                fill={FAIL_FILL}
                name="Fail"
                dot={{ r: 3, fill: FAIL_COLOR, strokeWidth: 0 }}
                activeDot={{ r: 5, fill: FAIL_COLOR, strokeWidth: 0 }}
              />
              <Area
                type="monotone"
                dataKey="pass"
                stackId="verdict"
                stroke={PASS_COLOR}
                fill={PASS_FILL}
                name="Pass"
                dot={{ r: 3, fill: PASS_COLOR, strokeWidth: 0 }}
                activeDot={{ r: 5, fill: PASS_COLOR, strokeWidth: 0 }}
              />
              <Area
                type="monotone"
                dataKey="passWithComment"
                stackId="verdict"
                stroke={PWC_COLOR}
                fill={PWC_FILL}
                name="Pass w/ comment"
                dot={{ r: 3, fill: PWC_COLOR, strokeWidth: 0 }}
                activeDot={{ r: 5, fill: PWC_COLOR, strokeWidth: 0 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </PageSection>

      <PageSection title="Average run duration" padded>
        {isLoading ? (
          <SkeletonBlock width="100%" height={260} />
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={durationData}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11 }}
                stroke={AXIS_COLOR}
                tickFormatter={formatDateTick}
              />
              <YAxis
                stroke={AXIS_COLOR}
                tick={{ fontSize: 11 }}
                tickFormatter={(value) => formatSeconds(value as number)}
              />
              <Tooltip
                content={(props: TooltipContentProps<number, string>) => (
                  <ChartTooltip
                    {...props}
                    labelFormatter={(label) => (typeof label === "string" ? formatDateTick(label) : "")}
                    valueFormatter={(value) => (typeof value === "number" ? formatSeconds(value) : value)}
                  />
                )}
              />
              <Line
                type="monotone"
                dataKey="avgDurationMs"
                stroke={DURATION_COLOR}
                strokeWidth={2}
                dot={{ r: 3, fill: DURATION_COLOR, strokeWidth: 0 }}
                activeDot={{ r: 5, fill: DURATION_COLOR, strokeWidth: 0 }}
                name="Avg duration"
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </PageSection>
    </div>
  );
}
